import { makeStyles } from '@material-ui/styles'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import MultilineTextField from 'core/components/validatedForm/MultilineTextField'
import Button from 'core/elements/button/Button'
import { topLeft } from 'core/elements/menu/defaults'
import Tooltip from 'core/elements/tooltip/Tooltip'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { Route } from 'core/plugins/route'
import colors from 'core/themes/base/colors'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import FontAwesomeIcon from 'pf9-ui-components/built/components/FontAwesomeIcon'
import PicklistField from 'pf9-ui-components/built/components/validatedForm/DropdownField'
import TextField from 'pf9-ui-components/built/components/validatedForm/TextField'
import { middleLeft } from 'pf9-ui-components/built/elements/menu/defaults'
import ModalForm from 'pf9-ui-components/built/elements/modal/ModalForm'
import Text from 'pf9-ui-components/built/elements/Text'
import React, { useCallback, useState } from 'react'
import useReactRouter from 'use-react-router'
import { createRepository, listRepositories, validateSshKey } from './actions'
import { getRepositoryRequestBody, validateSshKeyRequestBody } from './helper'
import SSHKeyTypePicklist from './SSHKeyTypePicklist'

const useStyles = makeStyles<Theme>((theme) => ({
  formFieldSection: {
    marginBottom: '32px',
    border: 'none',
    padding: 0,
  },
  content: {
    display: 'grid',
    gap: 16,
    gridAutoRows: 'max-content',
  },
  contentContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  iconWrapper: {
    display: 'flex',
    height: '32px',
    width: '32px',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  customFormFieldSection: {
    '& .content': {
      marginLeft: '0px',
    },
  },
  button: {
    textAlign: 'start',
    padding: 0,
    display: 'grid',
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 'none',
    border: 'none',
    cursor: 'pointer',
    borderColor: 'white',

    '& .button-text': {
      color: colors.blue[900],
      backgroundColor: 'white',
    },
    '&:hover, &.hover, &:active, &.active, &:focus, &.focus': {
      backgroundColor: 'white',
      borderColor: 'white',
      background: 'transparent',
      color: colors.blue[900],
    },
  },
  buttonContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  spinner: {
    color: colors.blue[900],
  },
  sshKeyIconWrapper: {
    display: 'flex',
    height: '24px',
    width: '24px',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  subTextContainer: {
    '& .inputFrame': {
      minHeight: 140,
      alignItems: 'flex-start',
      boxSizing: 'border-box',
      overflow: 'auto',
      width: '100%',
      resize: 'vertical',
    },
  },
}))

interface Props {
  addRoute: Route
}

export default function CreateRepositoryModal({ addRoute }: Props) {
  const { history } = useReactRouter()

  const imagePath = '/ui/images/circular-key.svg'
  const circularCheckImagePath = '/ui/images/right-tick-circle.svg'
  const circularCrossImagePath = '/ui/images/cross-circle.svg'

  const classes = useStyles()
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)
  const defaultParams = {
    repositoryUrl: '',
    keyName: '',
    description: '',
    keyType: 'rsa',
    sshKey: '',
  }
  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)
  const [isValidSshKey, setIsValidSshKey] = useState<boolean | undefined>(undefined)

  const { reload } = useListAction(listRepositories, {})
  const { update, updating, error, reset } = useUpdateAction(createRepository)
  const {
    update: updateSshKey,
    updating: updatingSshKey,
    error: errorSshKey,
    reset: resetSshKey,
  } = useUpdateAction(validateSshKey)

  const createRepositoryFn = async (params) => {
    const body = getRepositoryRequestBody(params)
    const { success, response } = await update({ body })
    return { success, response }
  }

  const validateSshkeyFn = async (params) => {
    const body = validateSshKeyRequestBody(params)
    const { success, response } = await updateSshKey({ body })
    return { success, response }
  }

  const validatesSshKey = useCallback(async () => {
    const { success } = await validateSshkeyFn(params)
    if (success) {
      setIsValidSshKey(true)
    } else {
      setIsValidSshKey(false)
    }
  }, [params, validateSshkeyFn])

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    resetSshKey()
    history.push(routeManager.routes.openstack.repository.path())
  }

  const submitForm = useCallback(async () => {
    // event?.stopPropagation()
    setAttemptedSubmit(true)

    const { success } = await createRepositoryFn(params)
    if (success) {
      handleClose()
      reload()
    }
    setAttemptedSubmit(false)
  }, [params, createRepositoryFn, handleClose, reload])

  return (
    <ModalForm
      route={addRoute}
      title={`Add Git Repository`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={attemptedSubmit}
      disableSubmit={!isValidSshKey}
      error={error || errorSshKey}
      submitTitle={`Add Git Repository`}
      maxWidth={470}
    >
      <>
        <div className={classes.formFieldSection}>
          <div className={classes.contentContainer}>
            <img
              className={classes.iconWrapper}
              alt="Add Repository"
              src={imagePath}
              data-testid="create-load-balancer-modal--img-0"
            />
            <div className={classes.content}>
              <Text variant="body2">
                Add a git repository URL along with an SSH key to authenticate and access private
                git repositories where your Terraform scripts are stored.
              </Text>
            </div>
          </div>
        </div>
        <FormFieldSection
          className={classes.customFormFieldSection}
          title="Git Repository Configuration"
        >
          <TextField
            id="repositoryUrl"
            label="Git Repository URL"
            placeholder="Enter Git Repository URL"
            onChange={getParamsUpdater('repositoryUrl')}
            value={params?.repositoryUrl}
            required
            enterKeyHint={undefined}
            nonce={undefined}
            onResize={undefined}
            onResizeCapture={undefined}
            crossOrigin={undefined}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
            info="This URL will be used to validate your SSH Key and access private repositories where your Terraform scripts are stored."
          />
          <TextField
            id="keyName"
            label="SSH Key Name"
            placeholder="Enter SSH Key Name"
            onChange={getParamsUpdater('keyName')}
            value={params?.keyName}
            required
            enterKeyHint={undefined}
            nonce={undefined}
            onResize={undefined}
            onResizeCapture={undefined}
            crossOrigin={undefined}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
          <TextField
            id="description"
            label="Description"
            placeholder="Enter Description"
            onChange={getParamsUpdater('description')}
            value={params?.description}
            // required
            enterKeyHint={undefined}
            nonce={undefined}
            onResize={undefined}
            onResizeCapture={undefined}
            crossOrigin={undefined}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />

          <PicklistField
            DropdownComponent={SSHKeyTypePicklist}
            name="keyType"
            id={`fixedIps.{idx}.keyType`}
            label="SSH Key Type"
            compact={false}
            value={params?.keyType}
            onChange={getParamsUpdater('keyType')}
            info="Select the type of SSH key for this git repository."
            required
          />
          <MultilineTextField
            id="sshKey"
            label="Private SSH Key"
            placeholder="Enter Private SSH Key"
            onChange={(val) => {
              getParamsUpdater('sshKey')(val)
              setIsValidSshKey(undefined)
            }}
            value={params?.sshKey}
            className={classes.subTextContainer}
            required
            // validations={[sshKeyTypeBasedValidator(params?.keyType || 'rsa')]}
            info={
              <div>
                Generate a new key with ssh-keygen -t rsa -m PEM, and paste the private key. The
                contents should begin with -----BEGIN RSA PRIVATE KEY-----
              </div>
            }
          />
          <div className={classes.buttonContainer}>
            {updatingSshKey && (
              <Tooltip align={topLeft.align} message={'Validating your SSH Key, please wait.'}>
                <FontAwesomeIcon size="lg" className={classes.spinner} spin>
                  circle-notch
                </FontAwesomeIcon>
              </Tooltip>
            )}

            {params.sshKey.length > 0 && (
              <>
                {isValidSshKey !== undefined && (
                  <img
                    className={classes.sshKeyIconWrapper}
                    alt="Add Repository"
                    src={
                      isValidSshKey
                        ? circularCheckImagePath
                        : isValidSshKey === false && circularCrossImagePath
                    }
                    data-testid="create-load-balancer-modal--img-0"
                  />
                )}

                <Text variant="body2">
                  {isValidSshKey
                    ? 'Your SSH Key is valid.'
                    : isValidSshKey === false && 'Invalid SSH Key.'}
                </Text>
              </>
            )}
            <Button
              className={classes.button}
              variant="secondary"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                validatesSshKey()
              }}
            >
              Validate SSH Key
            </Button>
          </div>
        </FormFieldSection>
      </>
    </ModalForm>
  )
}
